//@flow

// Vendors
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

// Components
import { FormActions, FormActionsItem, Trans } from '../..';
import { Modal } from '../../modals';
import { PaymentCardFormWrapper } from '../../../Payment';
import { TxButton, TxButtonMode } from 'texkit-ui';
import { TxPaymentCardForm, TxPaymentCardFormMode } from 'texkit-ui/forms';

// Actions
import {
  addPaymentAccount,
  getPaymentAccounts,
} from '../../../../reducers/payment/paymentActions';
import {
  closeModal,
  openHoustonErrorModal,
} from '../../../../reducers/modal/modalActions';
import { APP_CONSTANTS } from '../../../../lib/data';
import {
  AUTHOR_TYPES,
  pushMessageBanner,
} from '../../../../reducers/messageBanner/messageBannerActions';

// Helpers
import { getCurrentFormattedTime } from '../../../../lib/helpers';

import './add-payment-account-modal.scss';

const AddPaymentAccountModal = () => {
  const dispatch = useDispatch();
  const [cardData, setCardData] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormFilled, setIsFormFilled] = useState(false);
  const { selectedLanguage } = useSelector(state => ({
    selectedLanguage: state.language.selected,
  }));

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      paymentAccountId: cardData.token,
      cardType: APP_CONSTANTS.CARD_TYPES[cardData.cardtype] || '',
      cardLast4: cardData.token ? cardData.token.slice(-4) : '',
      expirationDate: values.expirationDate.replace('/', ''),
      paymentAccountName: values.name,
      address: {
        streetAddress: values.streetAddress,
        addressLine2: values.streetAddress2 || '',
        city: values.city,
        state: values.state,
        postalCode: values.zip,
      },
    };

    try {
      await dispatch(addPaymentAccount(payload));
      dispatch(getPaymentAccounts());
      dispatch(
        pushMessageBanner([
          {
            messageId: Date.now().toString(),
            textEnglish: '{"type": "Alerts", "id": "SuccessCardSaved"}',
            textSpanish: '{"type": "Alerts", "id": "SuccessCardSaved"}',
            startTime: getCurrentFormattedTime(),
            endTime: getCurrentFormattedTime(60),
            priority: '4',
            status: 'R',
            author: AUTHOR_TYPES.ACTION_RESULT,
            valid: true,
            dismissable: true,
            expiresIn: 60000,
          },
        ])
      );
      dispatch(closeModal('AddPaymentAccountModal'));
    } catch (err) {
      const options = {
        navigateOnClose: false,
      };
      dispatch(openHoustonErrorModal(options));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      name="AddPaymentAccountModal"
      className="add-payment-account-modal"
      disableOnClickOutside="true"
      closeButtonClassName="modal-close-button"
    >
      <h2 className="h1 modal-title">
        <Trans file="Payment" id="AddCardTitle" />
      </h2>
      <Formik
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {formikProps => {
          const { isSubmitting } = formikProps;
          const isSubmitDisabled =
            !isFormValid || !cardData.token || isSubmitting || !isFormFilled;

          return (
            <form noValidate>
              <PaymentCardFormWrapper formikProps={formikProps}>
                <TxPaymentCardForm
                  displayName="TxPaymentCardForm"
                  paymentMethodTitle={
                    <Trans file="Payment" id="CardInformationTitle" />
                  }
                  billingAddressTitle={
                    <Trans file="Payment" id="BillingAddressTitle" />
                  }
                  mode={TxPaymentCardFormMode.AccountSettingsAdd}
                  onCardFieldMessage={fdmsMessage => setCardData(fdmsMessage)}
                  spanish={selectedLanguage === 'es'}
                  onFormError={errors => {
                    const hasErrors = Object.keys(errors).length > 0;
                    setIsFormValid(!hasErrors);
                  }}
                  onFormFilled={isFilled => {
                    setIsFormFilled(isFilled);
                  }}
                />

                <FormActions>
                  <FormActionsItem>
                    <TxButton
                      mode={TxButtonMode.Primary}
                      onClick={() => formikProps.handleSubmit()}
                      disabled={isSubmitDisabled}
                    >
                      <Trans file="Payment" id="SavePaymentMethod" />
                    </TxButton>
                  </FormActionsItem>
                </FormActions>
              </PaymentCardFormWrapper>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddPaymentAccountModal;
